import { onUnmounted, ref } from "vue";

const position = (ev: MouseEvent | TouchEvent): [number, number] => {
  if (ev instanceof MouseEvent) {
    return [ev.clientX, ev.clientY];
  }

  if (ev instanceof TouchEvent) {
    return [ev.changedTouches[0].clientX, ev.changedTouches[0].clientY];
  }

  return [NaN, NaN];
};

const isLeftClick = (ev: MouseEvent | TouchEvent) => {
  if (ev instanceof MouseEvent) {
    return ev.button === 0;
  }

  return false;
};

const isTouchEvent = (ev: Event): boolean => {
  return window.TouchEvent && ev instanceof TouchEvent;
};

export interface DraggedState {
  x: number;
  y: number;
  currentX: number;
  currentY: number;
  startX: number;
  startY: number;
}

export const useDraggable = ({
  onDragStart,
  onDragMove,
  onDragEnd
}: {
  onDragStart?(state: DraggedState, ev: Event): unknown;
  onDragMove?(state: DraggedState, ev: Event): unknown;
  onDragEnd?(state: DraggedState, ev: Event): unknown;
} = {}) => {
  const x = ref(0);
  const y = ref(0);

  const startX = ref<number>();
  const startY = ref<number>();

  const currentX = ref<number>();
  const currentY = ref<number>();

  let bound = false;

  const getState = (): DraggedState => {
    return {
      startX: startX.value ?? 0,
      startY: startY.value ?? 0,
      currentX: currentX.value ?? 0,
      currentY: currentY.value ?? 0,
      x: x.value,
      y: y.value
    };
  };

  const disableEvent = (ev: Event) => {
    ev.preventDefault();

    return false;
  };

  const dragStart = (ev: MouseEvent | TouchEvent): void => {
    if (isTouchEvent(ev)) {
      document.addEventListener("touchmove", disableEvent, { passive: false });
      document.addEventListener("scroll", disableEvent);
    }

    if (isTouchEvent(ev) || isLeftClick(ev)) {
      const [newX, newY] = position(ev);

      startX.value = newX;
      startY.value = newY;

      setup();

      onDragStart?.(getState(), ev);
    }
  };

  const dragMove = (ev: MouseEvent | TouchEvent): void => {
    if (startX.value && startY.value) {
      const [newX, newY] = position(ev);

      currentX.value = newX;
      currentY.value = newY;

      x.value = newX - startX.value;
      y.value = newY - startY.value;

      onDragMove?.(getState(), ev);
    }
  };

  const dragEnd = (ev: MouseEvent | TouchEvent): void => {
    if (isTouchEvent(ev)) {
      document.removeEventListener("scroll", disableEvent);
      document.removeEventListener("touchmove", disableEvent);
    }

    onDragEnd?.(getState(), ev);

    reset();
  };

  const setup = (): void => {
    document.body.style.userSelect = "none";

    window.addEventListener("mousemove", dragMove);
    window.addEventListener("touchmove", dragMove);

    window.addEventListener("mouseup", dragEnd);
    window.addEventListener("touchend", dragEnd);
    bound = true;
  };

  const reset = (): void => {
    document.body.style.removeProperty("user-select");

    startX.value = undefined;
    startY.value = undefined;

    if (bound) {
      window.removeEventListener("mousemove", dragMove);
      window.removeEventListener("touchmove", dragMove);

      window.removeEventListener("mouseup", dragEnd);
      window.removeEventListener("touchend", dragEnd);
    }
  };

  onUnmounted(() => {
    reset();
  });

  return {
    x,
    y,
    dragStart
  };
};
